import { MouseEventHandler, type FocusEventHandler } from "react";

import { css, cx } from "@emotion/css";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { PaletteColors } from "@/theme/PaletteOverrides.ts";
import { Tooltip } from "@/uiKit/atoms/Tooltip.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { suffixId } from "@/uiKit/atoms/utils/ids.ts";

export type Option<T> = {
  value: T;
  label: string;
};

export type BaseChoiceItemProps<T> = {
  option: Option<T>;
  selected: boolean;
  onClick?: MouseEventHandler<HTMLElement>;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  tooltip?: string;
  disabled?: boolean;
  color?: PaletteColors;
};

export type ChoiceItemClasses = StyleClasses<
  "chip" | "selectedIcon" | "tooltip" | "tooltipText" | "tooltipWrapper" | "tooltipFloater"
>;

export type ChoiceItemProps<T> = AtomProps<BaseChoiceItemProps<T>, ChoiceItemClasses>;

export const ChoiceItem = <T,>({
  option,
  selected,
  tooltip,
  className,
  classes,
  ...props
}: ChoiceItemProps<T>): React.ReactElement => {
  const styles = useStyles(makeStyles, selected);

  const chip = (
    <Chip
      icon={selected ? <Icons.check className={cx(styles.icon, classes?.selectedIcon)} /> : undefined}
      color="primary"
      variant="outlined"
      className={cx(styles.chip, className, classes?.chip)}
      label={option.label}
      {...props}
    />
  );

  if (tooltip) {
    return (
      <Tooltip
        popupId={suffixId(props.id, "tooltip")}
        className={classes?.tooltip}
        floaterClassName={classes?.tooltipFloater}
        wrapperClassName={classes?.tooltipWrapper}
        content={<Typography className={classes?.tooltipText}>{tooltip}</Typography>}
        placement="top"
      >
        <span>{chip}</span>
      </Tooltip>
    );
  }

  return chip;
};

const makeStyles = (theme: Theme, selected: boolean) => ({
  chip: css({
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
    backgroundColor: selected ? theme.palette.background.default : theme.palette.common.white,
    "& > .MuiChip-icon": {
      margin: theme.spacing(0, -4, 0, -4),
    },
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
